import React, { useState } from "react";
import { Card, Table, Button, Tag, Space, message, DatePicker, Tooltip, Modal, Input } from "antd";
import styled from "styled-components";
import {
  DownloadOutlined,
  SearchOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import { useLojaSelecionada } from "../../../queries/useLojaSelecionada";

const { RangePicker } = DatePicker;

const moneyBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const API_BASE = "https://apitestecontabil.techspdv.com.br";
//const API_BASE = "http://localhost:8050";

const ExportacaoNotasEntradaTabela = () => {
  const accessToken = window.localStorage.getItem("accessToken");
  const { data: lojaSelecionada } = useLojaSelecionada();


  const [modalEmailVisible, setModalEmailVisible] = useState(false);
  const [emailDestino, setEmailDestino] = useState("");
  const [loadingEmail, setLoadingEmail] = useState(false);

  const lojaIdSafe =
    typeof lojaSelecionada === "string"
      ? lojaSelecionada.trim()
      : typeof lojaSelecionada === "object" && lojaSelecionada?.id
        ? String(lojaSelecionada.id).trim()
        : "";

  const cnpjCliente = lojaSelecionada?.cnpj || "";
  const temLojaSelecionada = lojaIdSafe.length > 0;

  const [periodo, setPeriodo] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const getHeaders = () => ({
    Authorization: `Bearer ${accessToken}`,
  });

  const baixarBlob = (blobData, fileName, type = "application/zip") => {
    const blob = new Blob([blobData], { type });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  const getFileName = (headers, fallback) => {
    const disposition = headers?.["content-disposition"];
    if (!disposition) return fallback;

    const match = disposition.match(/filename="?([^"]+)"?/);
    return match?.[1] || fallback;
  };

  const fetchNotas = async ({ page, size } = {}) => {
    if (!accessToken) {
      message.error("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!temLojaSelecionada) {
      setDataSource([]);
      setPagination((p) => ({ ...p, total: 0 }));
      setSelectedIds([]);
      return;
    }

    if (!periodo || periodo.length !== 2 || !periodo[0] || !periodo[1]) {
      message.warning("Selecione o período.");
      return;
    }

    const uiPage = page ?? pagination.current;
    const pageSize = size ?? pagination.pageSize;

    const dtIni = dayjs(periodo[0]).format("YYYY-MM-DD");
    const dtFim = dayjs(periodo[1]).format("YYYY-MM-DD");

    try {
      setLoading(true);

      const resp = await axios.get(`${API_BASE}/v1/notas-entrada`, {
        params: {
          lojaId: lojaIdSafe,
          cnpjCliente,
          dtIni,
          dtFim,
          page: uiPage - 1,
          size: pageSize,
        },
        headers: getHeaders(),
      });

      const pageData = resp.data || {};
      const content = Array.isArray(pageData.content) ? pageData.content : [];

      const rows = content.map((r) => ({
        id: String(r.id),
        cnpjCliente: r.cnpjCliente || r.cnpj_cliente,
        cnpjFornecedor: r.cnpjFornecedor || r.cnpj_fornecedor,
        nomeFornecedor: r.nomeFornecedor || r.nome_fornecedor,
        dataCaptura: r.dtCapturaNota || r.dt_captura_nota,
        dataNota: r.dtNota || r.dt_nota,
        numeroNota: r.numeroNota || r.numero_nota,
        serie: r.serie,
        valor: r.valor,
        chave: r.chave,
      }));

      setDataSource(rows);

      setPagination((prev) => ({
        ...prev,
        current: (pageData.number ?? uiPage - 1) + 1,
        pageSize: pageData.size ?? pageSize,
        total: pageData.totalElements ?? rows.length,
      }));
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        message.error("Sessão expirada. Faça login novamente.");
      } else {
        message.error("Erro ao buscar notas de entrada.");
      }

      setDataSource([]);
      setPagination((p) => ({ ...p, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const baixarXmlLinha = async (nota) => {
    if (!accessToken) {
      message.error("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!nota?.chave) {
      message.error("Chave da nota não encontrada.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/v1/notas-entrada/xml`, {
        params: {
          cnpjCliente,
          chave: nota.chave,
        },
        responseType: "blob",
        headers: getHeaders(),
      });

      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `XML-ENTRADA-${nota.chave}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      message.success("Download iniciado.");
    } catch (err) {
      console.error(err);
      message.error("Erro ao baixar XML.");
    } finally {
      setLoading(false);
    }
  };

  const baixarXmlLote = async () => {
    if (!selectedIds.length) {
      message.warning("Selecione pelo menos uma nota.");
      return;
    }

    const chavesSelecionadas = selectedIds;

    try {
      setLoading(true);

      const resp = await axios.post(
        `${API_BASE}/v1/notas-entrada/xml/lote`,
        chavesSelecionadas,
        {
          params: { cnpjCliente },
          headers: {
            ...getHeaders(),
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `XML-ENTRADA-LOTE-${Date.now()}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Erro ao baixar XML em lote.");
    } finally {
      setLoading(false);
    }
  };

  const exportarTudo = async () => {
    const dtIni = periodo?.[0]?.format("YYYY-MM-DD");
    const dtFim = periodo?.[1]?.format("YYYY-MM-DD");

    if (!dtIni || !dtFim) {
      message.warning("Selecione o período.");
      return;
    }

    try {
      setLoading(true);

      const resp = await axios.post(
        `${API_BASE}/v1/notas-entrada/xml/exportar`,
        null,
        {
          params: {
            cnpjCliente,
            dtIni,
            dtFim,
          },
          headers: getHeaders(),
          responseType: "blob",
        }
      );

      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `XML-ENTRADA-PERIODO-${Date.now()}.zip`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Erro ao exportar XML do período.");
    } finally {
      setLoading(false);
    }
  };


  const gerarRelatorio = async () => {
    try {
      const dtIni = periodo?.[0]?.format('YYYY-MM-DD');
      const dtFim = periodo?.[1]?.format('YYYY-MM-DD');

      if (!dtIni || !dtFim) return;

      const resp = await axios.get(
        `${API_BASE}/v1/notas-entrada/relatorio?cnpjCliente=${lojaSelecionada?.cnpj}&dtIni=${dtIni}&dtFim=${dtFim}`,
        {
          headers: getHeaders(),
          responseType: 'blob',
        }
      );

      const disposition = resp.headers['content-disposition'];
      let fileName = `RELATORIO-NOTAS-${Date.now()}.pdf`;

      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    }
  };

  const handleEnviarRelatorioNotasEntrada = async () => {
    const dtIni = periodo?.[0]?.format("YYYY-MM-DD");
    const dtFim = periodo?.[1]?.format("YYYY-MM-DD");

    if (!dtIni || !dtFim) {
      message.warning("Selecione um período.");
      return;
    }

    if (!emailDestino) {
      message.warning("Informe o email.");
      return;
    }

    setLoadingEmail(true);

    try {
      await axios.get(
        `${API_BASE}/v1/notas-entrada/relatorio-notas-entrada`,
        {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
          params: {
            para: emailDestino,
            cnpjCliente: lojaSelecionada?.cnpj,
            dtIni,
            dtFim,
          },
        }
      );

      message.success("Relatório enviado com sucesso!");
      setModalEmailVisible(false);
      setEmailDestino("");
    } catch (err) {
      console.error(err);
      message.error("Erro ao enviar email.");
    } finally {
      setLoadingEmail(false);
    }
  };

  const columns = [
    {
      title: "Data Nota",
      dataIndex: "dataNota",
      key: "dataNota",
      width: 160,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Fornecedor",
      dataIndex: "nomeFornecedor",
      key: "nomeFornecedor",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "CNPJ Fornecedor",
      dataIndex: "cnpjFornecedor",
      key: "cnpjFornecedor",
      width: 170,
      render: (v) => v || "-",
    },
    {
      title: "Nº/Série",
      key: "numeroSerie",
      width: 130,
      render: (_, r) =>
        r.numeroNota || r.serie ? `${r.numeroNota ?? "-"} / ${r.serie ?? "-"}` : "-",
    },
    {
      title: "Chave",
      dataIndex: "chave",
      key: "chave",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      width: 120,
      align: "right",
      render: (v) => (v != null ? moneyBRL(v) : "-"),
    },
    {
      title: "XML",
      key: "acoes",
      width: 90,
      align: "center",
      render: (_, nota) => (
        <Tooltip title="Baixar XML da nota de entrada">
          <BtnBlack
            shape="circle"
            icon={<DownloadOutlined />}
            onClick={() => baixarXmlLinha(nota)}
          />
        </Tooltip>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedIds,
    preserveSelectedRowKeys: true,
    onChange: (keys) => setSelectedIds(keys.map(String)),
    getCheckboxProps: () => ({
      disabled: !temLojaSelecionada,
    }),
  };

  const handleTableChange = (pag) => {
    fetchNotas({ page: pag.current, size: pag.pageSize });
  };

  return (
    <Page>
      <TopBar>
        <TitleArea>
          <h2 style={{ margin: 0 }}>Notas de Entrada</h2>
          <small style={{ color: "#777" }}>
            Busque notas de entrada por período, selecione e baixe os XMLs.
          </small>
        </TitleArea>

        <RightControls>
          <RangePicker
            value={periodo}
            onChange={(val) => setPeriodo(val)}
          />

          <BtnBlack
            icon={<SearchOutlined />}
            onClick={() => {
              setPagination((p) => ({ ...p, current: 1 }));
              fetchNotas({ page: 1 });
            }}
            disabled={!temLojaSelecionada}
          >
            Buscar
          </BtnBlack>

          <BtnBlack
            icon={<InboxOutlined />}
            onClick={baixarXmlLote}
            disabled={!temLojaSelecionada || !selectedIds.length}
          >
            Exportar Selecionadas
          </BtnBlack>

          <BtnBlack
            icon={<InboxOutlined />}
            onClick={exportarTudo}
            disabled={!temLojaSelecionada}
          >
            Exportar Tudo
          </BtnBlack>
          <BtnBlack
            icon={<InboxOutlined />}
            onClick={gerarRelatorio}
            disabled={!temLojaSelecionada}
          >
            Gerar Relatório
          </BtnBlack>

          <BtnBlack
            icon={<InboxOutlined />}
            onClick={() => setModalEmailVisible(true)}
            disabled={!temLojaSelecionada}
          >
            Enviar por Email
          </BtnBlack>
        </RightControls>
      </TopBar>

      <CardStyled>
        <Table
          rowKey="chave"
          rowSelection={rowSelection}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: temLojaSelecionada
              ? "Nenhuma nota de entrada encontrada no período."
              : "Selecione uma loja para buscar.",
          }}
        />
      </CardStyled>


      <Modal
        title="Enviar relatório por email"
        open={modalEmailVisible}
        onCancel={() => setModalEmailVisible(false)}
        onOk={handleEnviarRelatorioNotasEntrada}
        confirmLoading={loadingEmail}
        okText="Enviar"
        cancelText="Cancelar"
      >
        <Input
          placeholder="Digite o email"
          value={emailDestino}
          onChange={(e) => setEmailDestino(e.target.value)}
        />
      </Modal>
    </Page>





  );
};

export default ExportacaoNotasEntradaTabela;

const Page = styled.div`
  padding: 20px;
  margin-top: 95px;
  margin-bottom: 80px;
`;

const TopBar = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const RightControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const CardStyled = styled(Card)`
  border-radius: 14px !important;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
`;

const BtnBlack = styled(Button)`
  background: #000 !important;
  border-color: #000 !important;
  color: #fff !important;
  font-weight: 600;

  .anticon {
    color: #fff !important;
  }

  &:hover {
    background: #111 !important;
    border-color: #111 !important;
    color: #fff !important;
  }

  &:disabled {
    background: #ccc !important;
    border-color: #ccc !important;
    color: #666 !important;
  }
`;